import { Fragment, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type {
  MergeCardRelation,
  NeedsActionEntry,
  NeedsActionMergeCandidate,
  NeedsActionTriageAnswer,
  NeedsActionRenameProposal,
} from '@ni/shared';
import {
  useAnswerMergeCandidate,
  useUndoTriageAnswer,
  useAnswerRenameProposal,
  useResumeRun,
  type MergeCandidateVariables,
} from '../../api/hooks';
import { ResumeButton } from '../resume-button';
import {
  errorLine,
  hasCode,
  nameTakenSentence,
  resumeErrorLine,
  validationSentence,
} from '../../api/errors';
import { assertNever } from '../../lib/assert-never';
import {
  CANCEL,
  MERGE_CANDIDATE_CONFIRM_ACTION,
  MERGE_CANDIDATE_CONFIRM_HEAD,
  MERGE_CANDIDATE_CONFIRM_LINE,
  MERGE_CANDIDATE_CONFIRM_WORKING,
  MERGE_CANDIDATE_DIFFERENT,
  MERGE_CANDIDATE_DIFFERENT_WORKING,
  MERGE_CANDIDATE_KEEP_ASK,
  MERGE_CANDIDATE_KEEP_NOTE,
  MERGE_CANDIDATE_NEEDS_INFO,
  MERGE_CANDIDATE_READING_ASK,
  MERGE_CANDIDATE_RECORD_LEAD,
  MERGE_CANDIDATE_RELATED,
  MERGE_CANDIDATE_RELATED_ACTION,
  MERGE_CANDIDATE_RELATED_WORKING,
  MERGE_CANDIDATE_SAME,
  mergeCandidateNeedsInfoLine,
  mergeCandidateSideLine,
  MERGE_CANDIDATE_WHY,
  mergedAutomaticallyHead,
  TRIAGE_UNDO,
  TRIAGE_UNDO_WORKING,
  possibleDuplicatesHead,
  showMoreLine,
  NEEDS_REVIEW_EMPTY,
  NEEDS_REVIEW_GO,
  RUN_PICK_ONCHAIN_IDENTITY,
  RENAME_PROPOSAL_NO_WORKING,
  RENAME_PROPOSAL_TITLE,
  RENAME_PROPOSAL_YES,
  RENAME_PROPOSAL_YES_WORKING,
  renameProposalAsk,
  renameProposalNo,
  reviewBeforeBriefLine,
  RUN_CONFIRM_IDENTITY,
  RUN_MORE_DETAIL,
} from '../../lib/copy';
import { formatDay, plural } from '../../lib/format';
import { entityHref, renameProposalHref, subjectHref } from '../../lib/knowledge';
import { linkReadings, linkSentence } from '../../lib/knowledge-link';
import { isTerminal } from '../../lib/run-state';
import { ErrorLine } from '../ui/error-line';
import { Modal } from '../ui/modal';
import { WorkingButton } from '../ui/working-button';
import { EmptyLine } from '../ui/empty-line';
import { Button } from '../ui/button';
import './needs-action.css';

/**
 * The Home inbox (ui-design §4.1). The server ranks — blocking → queued →
 * identity kinds, plus `awaiting_*` and `failed` runs — and **the client
 * preserves array order**; nothing here sorts.
 *
 * Two collapses, and no others: the questions a **live** run raised are
 * represented by that run's own line ("N findings to review before the summary",
 * once it parks), and the queued rows belonging to no live run collapse into one
 * line at the position of the first of them. Both are counts of items the server
 * already sent, never a client-side hide.
 *
 * Every kind and state the projection can carry has a line or an explicit
 * silence; `assertNever` makes a new one a build failure rather than a row the
 * office never sees.
 */

export interface Row {
  key: string;
  what: string;
  /** The quiet go-link's word; absent where the row carries Resume instead. */
  go?: string;
  /** A failed run: the line reads in the attention color. */
  failed?: true;
  /** The run a Resume button acts on, for the failed row. */
  failedRunId?: string;
}

export function buildRows(entry: NeedsActionEntry): Row[] {
  const items = entry.items;

  const runItems = items.filter((item) => item.type === 'run');
  const runStates = new Set(runItems.map((item) => item.state));
  const liveRunIds = new Set(
    runItems.filter((item) => !isTerminal(item.state)).map((item) => item.run_id),
  );

  /**
   * The live run an open question belongs to, or null if it belongs to none.
   * Two tests, and both are load-bearing:
   *
   * - **the run id**, because a `claim_adjudication` can outlive the run that
   *   raised it (or have been raised outside one), and such a row is answered
   *   inline on the brief with nothing waiting on it — it is not part of any
   *   park and must not be counted into one;
   * - **that the run is still live**, because that is the predicate the *brief*
   *   uses. `subject-record.service.ts` withholds a live run's queued rows from
   *   the pinned view (§3e), so a row Home offered as "review it on the brief"
   *   while its run was live would deep-link to a page that does not show it.
   *   One predicate on both sides is the only way the two pages agree.
   *
   * Non-terminal, not `awaiting_adjudication`, and that is the §3f UX as decided:
   * a question raised during the facet pass appears on Home only once the run
   * reaches its park, as part of that run's own line ("N findings to review
   * before the summary"). While the run is merely working it shows nothing —
   * the whole point of batching was to stop asking the office one question at a
   * time as the run turned them up.
   *
   * Blocking rows fold on the same predicate, which is the rule they already
   * followed: they were never offered as standalone lines, because a blocking
   * finding is withheld on the run until it is answered.
   */
  const parkOf = (item: NeedsActionEntry['items'][number]): string | null =>
    item.type === 'decision' &&
    item.kind === 'claim_adjudication' &&
    item.run_id !== null &&
    liveRunIds.has(item.run_id)
      ? item.run_id
      : null;

  /* Runs parked on an on-chain identity pick: that question has its own line,
     so a park with no findings besides it says nothing more. */
  const onchainPickRuns = new Set(
    items.flatMap((item) =>
      item.type === 'decision' && item.kind === 'onchain_entity_pick' && item.run_id !== null
        ? [item.run_id]
        : [],
    ),
  );

  const parkedCounts = new Map<string, number>();
  for (const item of items) {
    const park = parkOf(item);
    if (park !== null) parkedCounts.set(park, (parkedCounts.get(park) ?? 0) + 1);
  }

  const queuedCount = items.filter(
    (item) =>
      item.type === 'decision' &&
      item.kind === 'claim_adjudication' &&
      item.blocking !== true &&
      parkOf(item) === null,
  ).length;

  const rows: Row[] = [];
  let queuedRendered = false;

  for (const item of items) {
    if (item.type === 'run') {
      switch (item.state) {
        case 'awaiting_identity_pick':
          rows.push({ key: item.run_id, what: RUN_CONFIRM_IDENTITY, go: NEEDS_REVIEW_GO.confirm });
          break;
        case 'awaiting_prose':
          rows.push({ key: item.run_id, what: RUN_MORE_DETAIL, go: NEEDS_REVIEW_GO.detail });
          break;
        case 'awaiting_adjudication': {
          // Every question the park is waiting on, blocking and queued alike.
          // Zero means the rows are there but say nothing about which run they
          // belong to; the uncounted line is honest about that rather than
          // claiming a number.
          const open = parkedCounts.get(item.run_id) ?? 0;
          if (open === 0 && onchainPickRuns.has(item.run_id)) break;
          rows.push({
            key: item.run_id,
            what: reviewBeforeBriefLine(open),
            go: NEEDS_REVIEW_GO.review,
          });
          break;
        }
        case 'failed':
          /* Generic, and deliberately so (§6.3). `shared/src/needs-action.ts`
             carries no kind field on a `run` item — the projection is an
             allow-list and every field on it was added for a reason — so wording
             this per-kind means widening the wire contract for a label. "Run
             failed" is true of a quick search, a dossier and a brief rewrite
             alike, and the row deep-links to the person, where the strip and the
             run history both say which kind it was. The line's job here is to
             get the office to the run, not to describe it. */
          /* A run the server will not resume (an unreadable identity match,
             2026-09-29) links to the record without a Resume: the record says
             why and offers the new quick search that is the way back. */
          rows.push(
            item.resumable
              ? { key: item.run_id, what: 'Run failed', failed: true, failedRunId: item.run_id }
              : {
                  key: item.run_id,
                  what: 'Run failed · contact Arbiter',
                  failed: true,
                  go: NEEDS_REVIEW_GO.open,
                },
          );
          break;
        case 'created':
        case 'running':
        // Between legs, with the successor already enqueued — working, by the
        // same reading as the two above, and asking the office for nothing.
        case 'checkpointed':
          // No line: a run that is working needs nothing. It is on the list only
          // so `parkOf` above can see that its questions are batched at a park
          // that has not been reached yet.
          break;
        default:
          assertNever(item.state, 'needs-action run state');
      }
      continue;
    }

    switch (item.kind) {
      case 'identity_confirm':
        // Dark under lock-or-ask: an identity is locked outright, with no
        // question, or asked as a pick list, which is the row above. Nothing
        // writes one of these any more, and a row written before the change
        // asks the office for nothing.
        break;
      case 'claim_adjudication':
        if (parkOf(item) !== null) break; // batched at the park; its run's line counts it
        if (item.blocking === true) break; // withheld on the run, carried by its line
        if (queuedRendered) break;
        queuedRendered = true;
        rows.push({
          key: item.decision_id,
          what: `${plural(queuedCount, 'finding', 'findings')} to review on the brief`,
          go: NEEDS_REVIEW_GO.review,
        });
        break;
      case 'identity_pick':
        // Only when no run line already says it (a decision can outlive its run).
        if (runStates.has('awaiting_identity_pick')) break;
        rows.push({
          key: item.decision_id,
          what: RUN_CONFIRM_IDENTITY,
          go: NEEDS_REVIEW_GO.confirm,
        });
        break;
      case 'identity_more_prose':
        if (runStates.has('awaiting_prose')) break;
        rows.push({ key: item.decision_id, what: RUN_MORE_DETAIL, go: NEEDS_REVIEW_GO.detail });
        break;
      case 'onchain_entity_pick':
        // Answered on the run strip, beside the findings it waits with.
        rows.push({
          key: item.decision_id,
          what: RUN_PICK_ONCHAIN_IDENTITY,
          go: NEEDS_REVIEW_GO.review,
        });
        break;
      default:
        assertNever(item.kind, 'needs-action decision kind');
    }
  }

  return rows;
}

function EntryGroup({ entry, rows }: { entry: NeedsActionEntry; rows: Row[] }) {
  const resume = useResumeRun(entry.subject.id);
  return (
    <EntryGroupView
      entry={entry}
      rows={rows}
      error={resume.isError && !hasCode(resume.error, 'RUN_NOT_RESUMABLE') ? resume.error : null}
      renderResume={(runId) => <ResumeButton runId={runId} resume={resume} />}
    />
  );
}

/**
 * **One subject's heading and its rows, with no write in it** — the same split
 * `MergeCandidateCardView` makes below, and made here for the same reason: the
 * one thing on this group worth pinning is **where its links go**, and the wired
 * half cannot be rendered without the app context this package's suite does not
 * stand up.
 *
 * Every link is the one address, built by `subjectHref` and never by hand. It
 * used to be written inline as `/people/:id/research`, which was true while a
 * subject could only be a person; the day an organisation's lookup failed, the
 * office pressed "Run failed" and landed on a **404** — a person's page carrying
 * a company's id (2026-09-10).
 */
export function EntryGroupView({
  entry,
  rows,
  error,
  renderResume,
}: {
  entry: NeedsActionEntry;
  rows: Row[];
  error: unknown;
  /** A failed row's Resume, priced like the record's (it reads app context). */
  renderResume: (runId: string) => ReactNode;
}) {
  const to = subjectHref(entry.subject);

  return (
    <div className="na-group">
      <div className="na-who">
        <Link className="na-name" to={to}>
          {entry.subject.name}
        </Link>
      </div>

      <div className="na-items">
        {rows.map((row) => (
          <div key={row.key} className={row.failed ? 'na-item failed' : 'na-item'}>
            <Link className="na-text" to={to}>
              {row.what}
            </Link>
            {row.failedRunId ? (
              <span className="na-actions">{renderResume(row.failedRunId)}</span>
            ) : row.go ? (
              <span className="na-go">
                {row.go}
                <GoMark />
              </span>
            ) : null}
          </div>
        ))}

        {error !== null ? <div className="na-note">{resumeErrorLine(error)}</div> : null}
      </div>
    </div>
  );
}

/** The go-link's chevron, which nudges right as the row is hovered. */
function GoMark() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M4.5 2.75 7.75 6 4.5 9.25"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ————————————————————————— "Are these the same?" ————————————————————————— */

/**
 * **Same asks twice; Different does not** (likely-merges note §2).
 *
 * The asymmetry is the whole rule, and it is about what the answer does rather
 * than about how sure the office is. `different` writes a verdict row and
 * changes nothing else — it is undone by an operator deleting one row. `same`
 * folds two records into one and leaves a redirect behind, and there is no undo
 * for it anywhere in the product, so it goes through the same confirm the delete
 * and the wrong-person do.
 *
 * A predicate rather than an `if` in the handler, so the rule can be pinned
 * without a DOM — this package's suite has no click.
 */
export function needsConfirmation(verdict: 'same' | 'different'): boolean {
  return verdict === 'same';
}

/**
 * **One thing the office can say about these two organisations**, as the whole
 * sentence it will be recorded as, and as the two words the wire takes.
 *
 * A reading rather than a relation and a direction, for the reason the knowledge
 * panel's Link door already gives (`lib/knowledge-link.ts`): a relation on its
 * own is half an answer between two organisations — "part of" does not say which
 * is the campus — and a reader asked for the halves separately has to put them
 * back together from two lit buttons. Said as a sentence, there is one thing to
 * agree with.
 */
export interface MergeCardReading {
  relation: MergeCardRelation;
  /** The side that is the whole, the governing body, or — where the tie does not
      rank — the counterparty slot. The wire's `whole_entity_id`. */
  wholeEntityId: string;
  /** The sentence itself, which is what the office reads and presses. */
  sentence: string;
}

/**
 * **Every sentence these two records can stand in**, in the order they are
 * offered — five, and the list is the Link door's own (`linkReadings`) read
 * through this card's two ids, so the two surfaces cannot drift into offering
 * different readings of the same fact.
 *
 * The direction is applied here, once: the Link door's `subject` is the *part*,
 * the governed side, so the **other** name is the whole — which is exactly what
 * `whole_entity_id` means on the wire. `a` stands where the panel's "here" does.
 *
 * **An organisation pair and nothing else**, which is why the kind is a
 * parameter rather than something the caller is trusted to have checked: there
 * is no fact in the catalog to write between two people or two events, the
 * contract's `related` arm refuses them, and a list of sentences about a pair of
 * people would be five buttons whose only answer is a 400. `linkReadings`
 * answers the empty list for every other pairing, and that is the guard — read
 * `org`/`org` it yields the three relations two organisations can stand in and
 * never `hosted`, whose other side is an event.
 */
export function mergeCardReadings(
  kind: NeedsActionMergeCandidate['kind'],
  a: NeedsActionMergeCandidate['a'],
  b: NeedsActionMergeCandidate['b'],
): MergeCardReading[] {
  if (kind !== 'org') return [];
  return linkReadings('org', 'org').map((reading) => ({
    /* `linkReadings` is typed over the catalog's four relations and asked here
       for the one pairing that yields three of them; the cast is the narrowing
       TypeScript cannot do from a table lookup. The real guard is that pairing
       plus the spec's own literal `READINGS` table, which pins the five
       sentences and the three words word for word. */
    relation: reading.relation as MergeCardRelation,
    wholeEntityId: reading.direction === 'subject' ? b.entity_id : a.entity_id,
    sentence: linkSentence(reading, a.name, b.name),
  }));
}

/** Two readings are the same reading when both of their words are. */
export const sameMergeCardReading = (one: MergeCardReading, other: MergeCardReading): boolean =>
  one.relation === other.relation && one.wholeEntityId === other.wholeEntityId;

/**
 * **The reading merge triage proposed**, as one of the card's own sentences, or
 * null. Affiliation does not rank its two sides, so it matches on the relation
 * alone; the other two match on which side is the whole.
 */
export function proposedReading(candidate: NeedsActionMergeCandidate): MergeCardReading | null {
  const proposed = candidate.triage?.proposed_reading;
  if (!proposed || candidate.triage?.proposed_answer !== 'related') return null;
  return (
    mergeCardReadings(candidate.kind, candidate.a, candidate.b).find(
      (option) =>
        option.relation === proposed.relation &&
        (option.relation === 'affiliated_with' ||
          option.wholeEntityId === proposed.whole_entity_id),
    ) ?? null
  );
}

/** A reading's key, for a list of buttons that has no id of its own. */
const readingKey = (reading: MergeCardReading): string =>
  `${reading.relation}:${reading.wholeEntityId}`;

/**
 * **What Cancel leaves behind**, on either of the card's two open things.
 *
 * A function rather than three `set`s inside the handler, for the reason
 * `needsConfirmation` is a predicate: this package's suite has no click, so a
 * rule that lives only inside a `useState` closure is true nowhere a test can
 * see it — and the rule here is the one that changed. Nothing is preselected on
 * the step any more, so a Cancel that kept the pick would reopen the step
 * showing an answer the office had already backed out of.
 */
export function afterCancel(): {
  confirming: false;
  relating: false;
  reading: null;
} {
  return { confirming: false, relating: false, reading: null };
}

/**
 * **The third answer as it goes on the wire**, built from the reading the office
 * picked.
 *
 * A function rather than an object literal inside the handler, for the reason
 * the predicate above is one: this package's suite has no click, and the thing
 * worth pinning here is not that a button calls something — it is the direction.
 * `whole_entity_id` names the whole or the governing body and the *other* side is
 * the part, so a body built the wrong way round would record "the University of
 * Texas is part of its Austin campus" and nothing on the page would say so.
 *
 * `null` while no side is picked: there is no answer yet, and the relation on
 * its own is not one.
 */
export function relatedAnswer(
  decisionId: string,
  relation: MergeCardRelation,
  wholeEntityId: string | null,
): MergeCandidateVariables | null {
  if (wholeEntityId === null) return null;
  return { decisionId, verdict: 'related', relation, whole_entity_id: wholeEntityId };
}

/**
 * **Which of the two names the confirm offers first** (organisation-identity
 * note §4), and `null` when it offers no choice at all.
 *
 * `null` means the two records are spelled the same, so there is nothing to
 * choose: joining them leaves the record called what both of them were called,
 * and a radio with one answer twice is a question that makes a reader look for a
 * difference that is not there. **Every `same_name` card lands here** — that
 * signal is the resolver's namesake refusal, whose two names are identical by
 * construction — so the list below never sees one.
 *
 * Otherwise, in order:
 *
 *  - **`contains`, `nickname` and `same_employer`** — one name is inside the
 *    other, or is the other's short form, so the **longer** one is the fuller
 *    spelling and is offered first. "T. Dupree Scovell" over "T. Dupree" is the
 *    case this was built for; "Clinton Phillips" over "Clint Phillips" is the
 *    same shape one signal along, and it is the one the office almost always
 *    wants.
 *  - **anything else** (`initials`, `similar`, or no signal at all) — the side
 *    the office **follows**, when exactly one of the two is followed. A record
 *    somebody has been working is the one whose name has been read, checked and
 *    written down by a person, and the other is a spelling that arrived off a
 *    reference.
 *  - **otherwise the first side**, which is the row the question was raised
 *    about. There is nothing in the question that distinguishes them, and a
 *    stable answer beats an arbitrary-looking one; the reader picks the other
 *    with one click either way.
 *
 * A predicate rather than an `if` in the component, so all four rules can be
 * pinned without a DOM — this package's suite has no click.
 */
/**
 * The signals on which the **longer** name is the fuller spelling of one thing,
 * rather than one of two ways of writing it. `similar` is not among them — a
 * typo is not shorter than the word it misspells — and neither is `initials`,
 * where the longer name is the expansion and the office may well want the
 * acronym.
 */
const LONGER_NAME_SIGNALS = new Set<string>(['contains', 'nickname', 'same_employer']);

export function keepNameChoice(candidate: NeedsActionMergeCandidate): string | null {
  const { a, b } = candidate;
  if (a.name === b.name) return null;
  if (LONGER_NAME_SIGNALS.has(candidate.signal ?? '')) {
    return a.name.length >= b.name.length ? a.entity_id : b.entity_id;
  }
  if (a.tracked !== b.tracked) return a.tracked ? a.entity_id : b.entity_id;
  return a.entity_id;
}

/**
 * **"Same" as it goes on the wire**, with the name the office chose on it.
 *
 * `keep_entity_id` is omitted rather than sent as the survivor's id when there
 * was no choice to make: the field means "the office said which name stands",
 * and a client that filled it in on a pair spelled the same way would be
 * recording an answer to a question nobody was asked.
 */
export function sameAnswer(
  decisionId: string,
  keepEntityId: string | null,
): MergeCandidateVariables {
  return keepEntityId === null
    ? { decisionId, verdict: 'same' }
    : { decisionId, verdict: 'same', keep_entity_id: keepEntityId };
}

/**
 * **When the card has left the list.** Two ways, and they are the same outcome:
 * this reader answered, or somebody else answered first and the POST came back
 * `DECISION_ALREADY_ANSWERED`.
 *
 * The second is deliberately not an error line. A 409 on this kind means the
 * question is settled — the contract says so, because the recorded answer is
 * written back wider than what was posted and so can never replay 200 (see
 * `EntityMergeCandidateRecordedAnswer`) — and a settled question is one the
 * office should stop being asked, not one it should be told off about. The
 * inbox refetch is already in flight behind it.
 */
export function mergeCardIsAnswered(answered: boolean, error: unknown): boolean {
  return answered || hasCode(error, 'DECISION_ALREADY_ANSWERED');
}

/**
 * One side of the pair: the name, and the little that can be said about it —
 * the only place on the row the name appears.
 *
 * **The name is a link to that side's own page**, because the card cannot answer
 * its own question. "Same" is the one answer in the product with no undo, and
 * the two lines here — a reason and a connection count — are what the *server*
 * noticed, not what the office knows; deciding needs the record behind each
 * name. `entityHref` picks the page the rest of the app would: a tracked
 * person's dossier where there is one, the knowledge-base entry otherwise.
 *
 * **In a new tab**, and that is the whole reason `target` is here. Following one
 * of these in place would unmount the inbox mid-question and drop every other
 * card the operator's backfill put on the page; the reader goes and looks, comes
 * back, and answers where they left off. `rel` is the pairing that goes with it.
 */
function MergeSide({
  side,
  kind,
}: {
  side: NeedsActionMergeCandidate['a'];
  kind: NeedsActionMergeCandidate['kind'];
}) {
  return (
    <div className="dup-side">
      <Link className="dup-name" to={entityHref(side, kind)} target="_blank" rel="noreferrer">
        {side.name}
      </Link>
      <span className="dup-meta">{mergeCandidateSideLine(side)}</span>
    </div>
  );
}

/**
 * **The card, with no write in it.** The same split `StatusControlView` makes,
 * and for the same reason: what is on the page is asserted here, and what the
 * buttons send is asserted on the wire in `api/client.spec.ts`.
 */
export function MergeCandidateCardView({
  candidate,
  confirming,
  relating,
  reading,
  keepEntityId,
  working,
  error,
  onSame,
  onDifferent,
  onRelated,
  onPickReading,
  onPickKeep,
  onConfirmSame,
  onConfirmRelated,
  onCancel,
}: {
  candidate: NeedsActionMergeCandidate;
  confirming: boolean;
  /** The second step is open: which of the five sentences about the pair is true. */
  relating: boolean;
  /** The sentence the office picked, or null while none is — nothing is chosen for them. */
  reading: MergeCardReading | null;
  /**
   * The side whose **name** the joined record keeps, or null when the two are
   * spelled the same and the confirm asks nothing about names.
   */
  keepEntityId: string | null;
  /** The verdict in flight, or null. */
  working: 'same' | 'different' | 'related' | null;
  error: unknown;
  onSame: () => void;
  onDifferent: () => void;
  onRelated: () => void;
  onPickReading: (reading: MergeCardReading) => void;
  onPickKeep: (entityId: string) => void;
  onConfirmSame: () => void;
  onConfirmRelated: () => void;
  onCancel: () => void;
}) {
  const busy = working !== null;

  /* Every sentence the pair can stand in, names filled in — five on an
     organisation pair, and empty on every other, which is also the only kind the
     step can be opened on. */
  const readings = mergeCardReadings(candidate.kind, candidate.a, candidate.b);

  /* The server's own sentence where it wrote one for a person to read, and the
     generic line otherwise. A `related` answer is the one thing on this card
     that can be refused with a 400 — the pair is not two organisations, or the
     id named is not one of these two — and those refusals are sentences. */
  const line = validationSentence(error) ?? errorLine(error);

  /* The reason and what would settle it are depth on demand: the names are on
     the row once, and both of these repeat them. Closed at rest — except on a
     held question, where what would settle it is the thing to read first.
     Until the reader toggles it, it follows the question, so a pair held while
     the page is open opens on the refetch. */
  /* **What merge triage proposed** (merge triage spec §3): its question is
     printed on the card in place of the probe's name sentence, its reason is
     the Why, and the answer it would give is the lit button. Nothing is
     answered for the office: the press is still theirs. */
  const proposed = candidate.triage?.proposed_answer ?? null;
  const lit = (answer: 'same' | 'different' | 'related'): 'act' | undefined =>
    (proposed === 'different' || proposed === 'related' ? proposed : 'same') === answer
      ? undefined
      : 'act';
  const reason = candidate.triage?.reason ?? candidate.reason;
  const held = candidate.needs_info !== null;
  const [toggled, setToggled] = useState<boolean | null>(null);
  const explaining = toggled ?? held;
  const explained = Boolean(reason) || held;

  return (
    <div className="dup">
      <div className="dup-row">
        <div className="dup-pair">
          <MergeSide side={candidate.a} kind={candidate.kind} />
          <MergeSide side={candidate.b} kind={candidate.kind} />
        </div>
        {/* No disclosure when there is nothing behind it: the contract allows the
          reason to be absent, and a Why that opens onto nothing is a dead control. */}
        {explained ? (
          <button
            type="button"
            className="dup-why"
            aria-expanded={explaining}
            onClick={() => setToggled(!explaining)}
          >
            {MERGE_CANDIDATE_WHY}
          </button>
        ) : null}
        {relating ? null : (
          <div className="merge-actions">
            <WorkingButton
              label={MERGE_CANDIDATE_SAME}
              workingLabel={MERGE_CANDIDATE_CONFIRM_WORKING}
              variant={lit('same')}
              tiny
              working={working === 'same'}
              disabled={busy && working !== 'same'}
              onClick={onSame}
            />
            <WorkingButton
              label={MERGE_CANDIDATE_DIFFERENT}
              workingLabel={MERGE_CANDIDATE_DIFFERENT_WORKING}
              variant={lit('different')}
              tiny
              working={working === 'different'}
              disabled={busy && working !== 'different'}
              onClick={onDifferent}
            />
            {/* **Organisations only**, and the card is where that is decided rather
              than the server's 400: there is no `org_relation` to write about two
              people or two events, and offering the office a button whose only
              answer is a refusal is offering them nothing. The pair's kind is on
              the card's own payload, so no read is needed to know. */}
            {candidate.kind === 'org' ? (
              <WorkingButton
                label={MERGE_CANDIDATE_RELATED}
                workingLabel={MERGE_CANDIDATE_RELATED_WORKING}
                variant={lit('related')}
                tiny
                working={working === 'related'}
                disabled={busy && working !== 'related'}
                onClick={onRelated}
              />
            ) : null}
          </div>
        )}
      </div>

      {candidate.triage?.question ? (
        <div className="dup-ask">{candidate.triage.question}</div>
      ) : null}

      {/* The server's sentence, verbatim — the whole of why these two are being
        asked about, and the card says nothing else about the match: no score, no
        signal name, no "likely". Then **what somebody said would settle it**
        (reconcile note §2.4), the same sort of thing: what is known about this
        pair. Rendered closed rather than not at all, so opening it is a toggle and
        not a fetch. */}
      {explained ? (
        <div className="dup-detail" hidden={!explaining}>
          {reason ? <div>{reason}</div> : null}
          {candidate.needs_info ? (
            <div className="dup-settle">
              {MERGE_CANDIDATE_NEEDS_INFO} {candidate.needs_info.text}
              <div className="src">
                {mergeCandidateNeedsInfoLine(
                  candidate.needs_info.by?.name ?? null,
                  candidate.needs_info.via,
                  formatDay(candidate.needs_info.at),
                )}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {relating ? (
        /* **The second step, on the card.** Not a modal: the confirm above is a
           modal because it is the last word before something with no undo, and
           this is a question — and one asked *about the two names on the row
           just above*, which a modal would cover over. The three answers are put away
           while it is open, so there is one thing to do on the card at a time. */
        <div className="merge-relate">
          <div className="relate-ask">{MERGE_CANDIDATE_READING_ASK}</div>
          {/* **The readings are the sentences**, names filled in, exactly as the
              knowledge panel's Link door offers them. Direction is not a second
              control: "A is part of B" and "B is part of A" are two of these,
              which is the only way a reader can be sure which way round they
              have said it.

              **Nothing is chosen for them.** `part_of` used to be lit from the
              start because the containment signal turns it up most often, which
              made the commonest answer one press — and made every other answer a
              correction of something the card had already said on the office's
              behalf. A sentence about two records is not a default. */}
          <div className="pills" role="group" aria-label={MERGE_CANDIDATE_READING_ASK}>
            {readings.map((option) => {
              const on = reading !== null && sameMergeCardReading(option, reading);
              return (
                <button
                  key={readingKey(option)}
                  type="button"
                  className={on ? 'pill on' : 'pill'}
                  aria-pressed={on}
                  disabled={busy}
                  onClick={() => onPickReading(option)}
                >
                  {option.sentence}
                </button>
              );
            })}
          </div>

          {/* What is about to be recorded, word for word — the sentence the fact
              will carry, and the primary line of the step rather than another
              instruction. Nothing until one is picked: a preview with a
              placeholder in it would be a sentence nobody said. */}
          {reading ? (
            <div className="relate-preview" aria-live="polite">
              <div className="relate-lead">{MERGE_CANDIDATE_RECORD_LEAD}</div>
              <div className="relate-sentence">{reading.sentence}</div>
            </div>
          ) : null}

          <div className="layer-actions">
            <WorkingButton
              label={MERGE_CANDIDATE_RELATED_ACTION}
              workingLabel={MERGE_CANDIDATE_RELATED_WORKING}
              working={working === 'related'}
              disabled={reading === null || (busy && working !== 'related')}
              onClick={onConfirmRelated}
            />
            {/* The way back to the three answers, live while the write runs, as
                the confirm's own Cancel is. */}
            <Button variant="act" onClick={onCancel}>
              {CANCEL}
            </Button>
          </div>
        </div>
      ) : null}

      {/* The server's own sentence, not a line of ours: `MERGE_REFUSED` names
          what stopped the fold, and `errorLine` falls through to the message for
          exactly this reason. The card stays — the question is still open.

          It is said once, where the reader is looking: the confirm stays open
          across the write (the sibling pattern in `correct-this.tsx`), so while
          it is open the refusal belongs inside it, and the card takes the line
          back the moment the modal is gone. */}
      {error && !confirming ? <div className="na-note">{line}</div> : null}

      {confirming ? (
        <Modal label={MERGE_CANDIDATE_CONFIRM_HEAD} narrow onClose={onCancel}>
          <div className="modal-head">{MERGE_CANDIDATE_CONFIRM_HEAD}</div>
          <div className="modal-line">{MERGE_CANDIDATE_CONFIRM_LINE}</div>

          {/* **What answering "Same" would do, said before the office says it**
              (fold-tracked-people note §7) — one server-owned sentence,
              verbatim, and the same sentence the connector reads back.

              Here because here is the last place before the press, and only
              here: "Different" and "Related" fold nothing, so the sentence
              belongs on the one road that does. Above the name choice and the
              button, and below the line that says it cannot be undone — the
              general fact about the act, then what it does to *these two*.

              It is not a summary of the card. A fold now unbinds the losing
              side's identity, supersedes its brief, moves its runs and its
              status and changes which id the record lives at, and none of that
              is anywhere on a card showing two names and a reason. Which is
              also why nothing on this side composes it: the sentence names the
              survivor by the server's own ordering, which no reader of this
              payload can work out.

              Null gets no row — either record gone since the page loaded, and
              the answer road will say so rather than fold. No placeholder: an
              empty box under the confirm line reads as a rendering fault, and a
              line invented here to fill it would be a promise nobody made. */}
          {candidate.fold_readback ? (
            <div className="modal-line">{candidate.fold_readback}</div>
          ) : null}

          {/* **And which name the one record answers to** (organisation-identity
              note §4), asked here because it is the second half of the same act
              and not a separate question — the last thing looked at before a
              join with no undo.

              Nothing at all when the two are spelled the same: `keepEntityId` is
              null exactly then, and a radio with one answer twice would make a
              reader hunt for a difference that is not there.

              Radios and not the pills the step above uses: this is one choice
              out of two with one of them already made, which is what a radio
              group is, and it has to read as a *setting on the join* rather than
              as a third button beside "Join them". By name only — the office
              never sees an id. */}
          {keepEntityId !== null ? (
            <div className="modal-choice">
              <div className="choice-ask">{MERGE_CANDIDATE_KEEP_ASK}</div>
              {/* The group carries the question as its name, as the two pickers
                  on the card do. Without it a screen reader reaches two radios
                  called "HBS" and "Harvard Business School" with nothing saying
                  what is being asked about them — the ask above is a `div` and
                  is announced, if at all, as unrelated text somewhere before
                  them. */}
              <div role="radiogroup" aria-label={MERGE_CANDIDATE_KEEP_ASK}>
                {[candidate.a, candidate.b].map((side) => (
                  <label className="picker-option" key={side.entity_id}>
                    <input
                      type="radio"
                      name={`keep-name-${candidate.decision_id}`}
                      value={side.entity_id}
                      checked={keepEntityId === side.entity_id}
                      disabled={busy}
                      onChange={() => onPickKeep(side.entity_id)}
                    />
                    <span>{side.name}</span>
                  </label>
                ))}
              </div>
              {/* Why the other one is safe to pass over, which is the whole
                  reason there is a choice here to make. */}
              <div className="choice-note">{MERGE_CANDIDATE_KEEP_NOTE}</div>
            </div>
          ) : null}

          <div className="modal-actions">
            <WorkingButton
              label={MERGE_CANDIDATE_CONFIRM_ACTION}
              workingLabel={MERGE_CANDIDATE_CONFIRM_WORKING}
              variant="danger"
              working={working === 'same'}
              onClick={onConfirmSame}
            />
            {/* Never disabled while the join runs. The write is the reader's to
                walk away from, as it is on every other confirm in the app, and
                the answer is idempotent by contract either way. */}
            <Button variant="act" onClick={onCancel}>
              {CANCEL}
            </Button>
          </div>
          {error ? <ErrorLine error={error} /> : null}
        </Modal>
      ) : null}
    </div>
  );
}

function MergeCandidateCard({ candidate }: { candidate: NeedsActionMergeCandidate }) {
  const answer = useAnswerMergeCandidate();
  const [confirming, setConfirming] = useState(false);
  const [answered, setAnswered] = useState(false);
  /* The second step, and the one pick it collects: which of the five sentences
     about this pair is true. Nothing is chosen to start with — it is the whole
     question, and a lit answer the office did not give is one they have to
     notice before they can disagree with it. */
  const [relating, setRelating] = useState(false);
  const [reading, setReading] = useState<MergeCardReading | null>(() => proposedReading(candidate));
  /* The name the join keeps, with the answer the question usually has already in
     it (`keepNameChoice`). Null the whole way through when the two records are
     spelled the same — there is nothing to ask and nothing to send. */
  const [keepEntityId, setKeepEntityId] = useState<string | null>(() => keepNameChoice(candidate));

  /* The card leaves the list the moment it is answered, rather than waiting for
     the inbox refetch: the question is gone, and a card that stayed on screen
     for another second would be offering to answer it again. */
  if (mergeCardIsAnswered(answered, answer.error)) return null;

  /**
   * The confirm is **not** closed before the write. It stays up across the whole
   * join, its action button carrying the working label, and closes on success —
   * the pattern `correct-this.tsx` and the "Wrong person" confirm in
   * `research.tsx` already set. Closing first would drop the reader back onto a
   * card that looks untouched while the fold is still running, and would put a
   * refusal somewhere other than where they were looking.
   *
   * Both state changes land in one batch on success, so the card is gone in the
   * same commit that unmounts the modal: the modal's focus-return finds its
   * opener disconnected and leaves focus alone, rather than handing it to a
   * button that is disabled or about to vanish (`ui/modal.tsx`).
   */
  const act = (verdict: 'same' | 'different') => {
    answer.mutate(
      verdict === 'same'
        ? sameAnswer(candidate.decision_id, keepEntityId)
        : { decisionId: candidate.decision_id, verdict },
      {
        onSuccess: () => {
          setConfirming(false);
          setAnswered(true);
        },
      },
    );
  };

  /** One door for both buttons; the predicate above decides which asks first. */
  const choose = (verdict: 'same' | 'different') => {
    if (needsConfirmation(verdict)) setConfirming(true);
    else act(verdict);
  };

  /**
   * The third answer, once the sentence is in hand. The step stays open across
   * the write, like the confirm above and for the same reason: a refusal — the
   * pair is not two organisations after all — belongs where the reader is
   * looking, and the pick they would have to make again is still on screen.
   */
  const relate = () => {
    if (reading === null) return;
    const body = relatedAnswer(candidate.decision_id, reading.relation, reading.wholeEntityId);
    if (body === null) return;
    answer.mutate(body, {
      onSuccess: () => {
        setRelating(false);
        setAnswered(true);
      },
    });
  };

  return (
    <MergeCandidateCardView
      candidate={candidate}
      confirming={confirming}
      relating={relating}
      reading={reading}
      keepEntityId={keepEntityId}
      working={answer.isPending ? (answer.variables?.verdict ?? null) : null}
      error={answer.isError ? answer.error : null}
      onSame={() => choose('same')}
      onDifferent={() => choose('different')}
      onRelated={() => setRelating(true)}
      onPickReading={setReading}
      onPickKeep={setKeepEntityId}
      onConfirmSame={() => act('same')}
      onConfirmRelated={relate}
      /* One way out for both, and {@link afterCancel} is the whole of what it
         leaves behind. Cancel on the step is "not this, then", and it takes the
         sentence with it: nothing was chosen when the step opened, and a step
         reopened with a sentence still lit would be showing an answer the office
         had already backed out of.

         **Including mid-write**, which the button stays live for: a Cancel
         pressed while the relation is being recorded drops the pick along with
         the step, so the reader who comes back to the card finds it as they
         found it the first time. That is the right outcome — they cancelled —
         and the write itself is already past the point where this page can call
         it back either way. */
      onCancel={() => {
        const next = afterCancel();
        setConfirming(next.confirming);
        setRelating(next.relating);
        setReading(next.reading);
      }}
    />
  );
}

/**
 * How many likely-merge rows show before the rest fold behind "Show N more".
 * The operator's backfill can put a dozen on the page at once, and they are
 * housekeeping that waits for nothing.
 */
export const DUPLICATES_SHOWN = 3;

/**
 * **Every likely-merge question under one head** — "Possible duplicates · N" in
 * the name column, as a person's name heads their rows, and one compact row per
 * pair beside it. The first few show; the rest wait behind one line.
 *
 * The rows are passed in as a render function, the split `EntryGroupView` makes
 * with its Resume: the wired card needs app context this package's suite does
 * not stand up, and what is worth pinning here is the head and the fold.
 *
 * The count is what the server sent, less any question answered here: the answer
 * drops its row from the inbox read (`useAnswerMergeCandidate`), so the head
 * comes down with the card rather than at the refetch.
 */
export function PossibleDuplicates({
  candidates,
  renderCandidate,
}: {
  candidates: NeedsActionMergeCandidate[];
  renderCandidate: (candidate: NeedsActionMergeCandidate) => ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? candidates : candidates.slice(0, DUPLICATES_SHOWN);
  const folded = candidates.length - shown.length;

  return (
    <div className="na-group na-dups">
      <div className="na-who">
        <span className="sc na-label">{possibleDuplicatesHead(candidates.length)}</span>
      </div>
      <div className="na-items">
        {shown.map((candidate) => (
          <Fragment key={candidate.decision_id}>{renderCandidate(candidate)}</Fragment>
        ))}
        {folded > 0 ? (
          <button type="button" className="linkish dup-more" onClick={() => setExpanded(true)}>
            {showMoreLine(folded)}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* ————————————————————————— merged automatically ————————————————————————— */

/**
 * **What merge triage settled without asking**, in the last 30 days (merge
 * triage spec §3). One server-written sentence each, with its reason, and an
 * Undo: a merge is unmerged, a "different" goes back on the list as a question.
 * The duplicates group's styling, so it reads as the same kind of thing.
 *
 * Folded by default: these are settled, not waiting on anyone, so the head and
 * its count stand in for the list until somebody opens it to check or undo one.
 */
export function MergedAutomaticallyView({
  entries,
  open,
  onToggle,
  working,
  error,
  onUndo,
}: {
  entries: NeedsActionTriageAnswer[];
  open: boolean;
  onToggle: () => void;
  /** The entry whose Undo is in flight, or null. */
  working: string | null;
  /** The entry whose Undo failed, and why. */
  error: { decisionId: string; error: unknown } | null;
  onUndo: (entry: NeedsActionTriageAnswer) => void;
}) {
  return (
    <div className="na-group na-dups">
      <div className="na-who">
        <button
          type="button"
          className="sc na-label na-fold"
          aria-expanded={open}
          onClick={onToggle}
        >
          {mergedAutomaticallyHead(entries.length)}
        </button>
      </div>
      {open ? (
        <div className="na-items">
          {entries.map((entry) => (
            <div className="dup" key={entry.decision_id}>
              <div className="dup-row">
                <div className="dup-pair">{entry.sentence}</div>
                <div className="merge-actions">
                  <WorkingButton
                    label={TRIAGE_UNDO}
                    workingLabel={TRIAGE_UNDO_WORKING}
                    variant="act"
                    tiny
                    working={working === entry.decision_id}
                    disabled={working !== null && working !== entry.decision_id}
                    onClick={() => onUndo(entry)}
                  />
                </div>
              </div>
              {error?.decisionId === entry.decision_id ? (
                <div className="na-note">{errorLine(error.error)}</div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function MergedAutomatically({ entries }: { entries: NeedsActionTriageAnswer[] }) {
  const [open, setOpen] = useState(false);
  const undo = useUndoTriageAnswer();
  const working = undo.isPending ? (undo.variables?.decisionId ?? null) : null;
  return (
    <MergedAutomaticallyView
      entries={entries}
      open={open}
      onToggle={() => setOpen(!open)}
      working={working}
      error={
        undo.isError && undo.variables
          ? { decisionId: undo.variables.decisionId, error: undo.error }
          : null
      }
      onUndo={(entry) =>
        undo.mutate({
          undo: entry.undo,
          decisionId: entry.decision_id,
          mergeDecisionId: entry.merge_decision_id,
        })
      }
    />
  );
}

/* ————————————————————————— the rename proposal ————————————————————————— */

/**
 * **The card, with no write in it** — `MergeCandidateCardView`'s split, beside
 * `MergeCandidateCardView`, for the same reason: what is on the page is asserted
 * here as markup, and what the buttons send is asserted on the wire.
 *
 * Four things and no more. The claim ("This looks like it is really called
 * Pogue Holdings."); what the run read it off, verbatim and muted, because it is
 * the run's word and not the app's; the record, as a link, since the card cannot
 * answer its own question any more than the merge card can; and the two answers.
 * No id, no confidence, no "the lookup found".
 */
export function RenameProposalCardView({
  proposal,
  working,
  error,
  onYes,
  onNo,
}: {
  proposal: NeedsActionRenameProposal;
  /** The answer in flight, as the office's word for it, or null. */
  working: 'rename' | 'keep' | null;
  error: unknown;
  onYes: () => void;
  onNo: () => void;
}) {
  const busy = working !== null;
  /* The name is the whole of what a refusal here has to say, so the sentence
     that names it is preferred over the code's generic line — the rename
     modal's own reading (`nameTakenSentence`). `NAME_BUSY` and
     `RENAME_TARGET_GONE` carry their sentence as the message and come through
     `errorLine`'s default arm, which is why one expression covers all three. */
  const line = nameTakenSentence(error) ?? errorLine(error);

  return (
    <div className="na-group na-rename">
      <div className="na-who">
        <span className="sc na-label">{RENAME_PROPOSAL_TITLE}</span>
      </div>

      <div className="na-items">
        <div className="merge-q">{renameProposalAsk(proposal.to_name)}</div>

        {/* Nullable by contract — the kind is also what the office's own door
            files — and an absent one gets no row rather than an empty one. */}
        {proposal.evidence ? <div className="src">{proposal.evidence}</div> : null}

        <div className="merge-sides">
          {/* In a new tab, as the merge card's two names are: following it in
              place would unmount the inbox in the middle of the question. */}
          <div className="merge-side">
            <Link
              className="merge-name"
              to={renameProposalHref(proposal)}
              target="_blank"
              rel="noreferrer"
            >
              {proposal.from_name}
            </Link>
          </div>
          <div className="merge-actions">
            <WorkingButton
              label={RENAME_PROPOSAL_YES}
              workingLabel={RENAME_PROPOSAL_YES_WORKING}
              working={working === 'rename'}
              disabled={busy && working !== 'rename'}
              onClick={onYes}
            />
            {/* No confirm. A rename is not the merge's one-way door — the old
                spelling stays as a redirect either way — so both answers are one
                press, and the No says which name it keeps. */}
            <WorkingButton
              label={renameProposalNo(proposal.from_name)}
              workingLabel={RENAME_PROPOSAL_NO_WORKING}
              working={working === 'keep'}
              disabled={busy && working !== 'keep'}
              onClick={onNo}
            />
          </div>
        </div>

        {/* **The card stays.** A 409 here is a rename that did not happen — the
          name was taken between the run proposing it and the office answering,
          the lock timed out, or the record was folded away — and the server
          rolls the answer back with it ("As grounded" 7), so the question is
          still open and the card is still the place to answer it. */}
        {error ? <div className="na-note">{line}</div> : null}
      </div>
    </div>
  );
}

function RenameProposalCard({ proposal }: { proposal: NeedsActionRenameProposal }) {
  const answer = useAnswerRenameProposal(proposal.entity_id);
  const [answered, setAnswered] = useState(false);

  /* Gone the moment it is answered, rather than at the inbox refetch — the merge
     card's rule, and `DECISION_ALREADY_ANSWERED` means the same thing here:
     somebody else answered first, and a settled question is one to stop asking
     rather than one to complain about. */
  if (mergeCardIsAnswered(answered, answer.error)) return null;

  const act = (confirmed: boolean) => {
    answer.mutate(
      { decisionId: proposal.decision_id, confirmed },
      { onSuccess: () => setAnswered(true) },
    );
  };

  return (
    <RenameProposalCardView
      proposal={proposal}
      working={answer.isPending ? (answer.variables?.confirmed ? 'rename' : 'keep') : null}
      error={answer.isError ? answer.error : null}
      onYes={() => act(true)}
      onNo={() => act(false)}
    />
  );
}

export function NeedsAction({
  entries,
  mergeCandidates = [],
  renameProposals = [],
  mergedAutomatically = [],
}: {
  entries: NeedsActionEntry[];
  /**
   * The likely-merge questions, which the projection carries **beside** the
   * per-subject entries rather than inside them (`shared/src/needs-action.ts`):
   * the two nodes in one are as often organisations or events as people, and
   * neither need be somebody the office follows, so there is no name to file
   * them under. The server orders them — same-employer questions first, then
   * oldest first, stable between reads — and the client does not re-sort.
   */
  mergeCandidates?: NeedsActionMergeCandidate[];
  /**
   * The lookup's name proposals, carried **beside** the per-subject entries for
   * the merge questions' reason exactly (`shared/src/needs-action.ts`): the
   * record one is about is as often an organisation, which has no status to
   * print a heading under. Oldest first, and the client does not re-sort.
   */
  renameProposals?: NeedsActionRenameProposal[];
  /** Merge triage's own answers from the last 30 days, newest first, each with an Undo. */
  mergedAutomatically?: NeedsActionTriageAnswer[];
}) {
  /* The empty line is keyed on the **rendered rows**, not on the entries. An
     entry can arrive carrying items and produce no line at all — a live run's
     questions fold into a park it has not reached, a blocking row is withheld on
     its run — and "Nothing to review." has to stay true in that case rather
     than leaving the section blank. */
  const groups = entries
    .map((entry) => ({ entry, rows: buildRows(entry) }))
    .filter((group) => group.rows.length > 0);

  if (
    groups.length === 0 &&
    mergeCandidates.length === 0 &&
    renameProposals.length === 0 &&
    mergedAutomatically.length === 0
  ) {
    return <EmptyLine>{NEEDS_REVIEW_EMPTY}</EmptyLine>;
  }

  /* The merge questions go **last**, under the people. The rows above are what
     research and the office's own work turned up about somebody they follow, and
     the server has already ranked them blocking-first; a question about whether
     two organisations are one organisation is housekeeping on the graph and
     waits for nothing. Putting it on top would push a held finding below the
     fold behind twelve of them the first time the operator's backfill runs. */
  return (
    <div>
      {groups.map((group) => (
        <EntryGroup key={group.entry.subject.id} entry={group.entry} rows={group.rows} />
      ))}
      {mergeCandidates.length > 0 ? (
        <PossibleDuplicates
          candidates={mergeCandidates}
          renderCandidate={(candidate) => <MergeCandidateCard candidate={candidate} />}
        />
      ) : null}
      {/* Last, under the merge questions, for the same reason those go under the
          people: a name to confirm is housekeeping on the graph and waits for
          nothing, where every row above is a finding somebody is held up on. */}
      {renameProposals.map((proposal) => (
        <RenameProposalCard key={proposal.decision_id} proposal={proposal} />
      ))}
      {/* Last of all: nothing here asks anything; it reports what was settled
          and offers the way back. */}
      {mergedAutomatically.length > 0 ? (
        <MergedAutomatically entries={mergedAutomatically} />
      ) : null}
    </div>
  );
}
